import React from 'react';
import { ArrowRight, Zap, Brain, Shield, Rocket, Star, Globe } from 'lucide-react';

export default function WhatmakesUsUnique() {
  return (
    <section className="py-24 px-6 bg-white relative overflow-hidden">
      {/* Floating Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full -translate-x-48 -translate-y-48 opacity-60"></div>
      <div className="absolute top-1/2 right-0 w-80 h-80 bg-gradient-to-bl from-purple-100 to-pink-100 rounded-full translate-x-40 opacity-60"></div>
      <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-gradient-to-tr from-orange-100 to-yellow-100 rounded-full translate-y-32 opacity-60"></div>
      
      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-full px-6 py-3 mb-6">
            <Star className="w-5 h-5 text-blue-600" />
            <span className="text-blue-700 font-semibold">What Sets Us Apart</span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Why We're
            <span className="relative inline-block mx-4">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Uniquely
              </span>
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-full"></div>
            </span>
            Different
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-poppins">
            We don't just follow trends—we create them. Our revolutionary approach combines cutting-edge innovation with proven results to deliver experiences that transform businesses.
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Feature 1 - Lightning Speed */}
          <div className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 overflow-hidden border border-gray-100">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-orange-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                3x Faster
              </div>
            </div>
            
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                <Zap className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning-Fast Execution</h3>
              <p className="text-gray-600 leading-relaxed mb-6 font-poppins">
                We deliver results 3x faster than traditional agencies through our proprietary rapid-development methodology and AI-assisted workflows.
              </p>
              
              <div className="flex items-center text-orange-600 font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <span>See how we do it</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>

          {/* Feature 2 - AI Innovation */}
          <div className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 overflow-hidden border border-gray-100">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                AI-Powered
              </div>
            </div>
            
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Next-Gen AI Integration</h3>
              <p className="text-gray-600 leading-relaxed mb-6 font-poppins">
                Harness the power of advanced machine learning and AI technologies that adapt, learn, and evolve with your business needs in real-time.
              </p>
              
              <div className="flex items-center text-blue-600 font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <span>Explore AI solutions</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>

          {/* Feature 3 - Future-Proof */}
          <div className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 overflow-hidden border border-gray-100">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-pink-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                Future-Ready
              </div>
            </div>
            
            <div className="relative">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                <Rocket className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Future-Proof Architecture</h3>
              <p className="text-gray-600 leading-relaxed mb-6 font-poppins">
                Build scalable, adaptable solutions designed to evolve with emerging technologies and grow seamlessly with your expanding business.
              </p>
              
              <div className="flex items-center text-purple-600 font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <span>Learn about scalability</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>

        {/* Unique Value Proposition Banner */}
        <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 rounded-3xl p-12 text-center overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-500 rounded-full opacity-10 -translate-y-36 animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500 rounded-full opacity-10 translate-y-32 animate-pulse delay-1000"></div>
          
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 mb-6">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-white font-semibold">Our Promise</span>
            </div>
            
            <h3 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              The Only Team That Guarantees
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                Results in 30 Days
              </span>
              <span className="text-white"> or It's Free</span>
            </h3>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-10 leading-relaxed font-poppins">
              We're so confident in our revolutionary approach that we guarantee measurable, impactful results within 30 days—or you don't pay a penny.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[
                { icon: Shield, text: "30-Day Guarantee" },
                { icon: Zap, text: "Instant Results" },
                { icon: Globe, text: "Global Support" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 hover:bg-white/20 transition-all duration-300">
                  <item.icon className="w-5 h-5 text-cyan-400" />
                  <span className="text-white font-semibold">{item.text}</span>
                </div>
              ))}
            </div>
            
            <button className="group bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold px-10 py-4 rounded-full shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105">
              <span className="flex items-center gap-2">
                Claim Your Guarantee
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 text-center">
          {[
            { number: "300%", label: "Faster Results" },
            { number: "99.9%", label: "Uptime Guarantee" },
            { number: "24/7", label: "Expert Support" },
            { number: "∞", label: "Scalability" }
          ].map((stat, index) => (
            <div key={index} className="group">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}